import React from "react";
import FAQAccordion from '../FAQAccordion';

class GlacierInfo extends React.Component {
  render() {
    return (
      <React.Fragment>
        <section className="about-us pt-trip gray-light-bg">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-md-10">

                <p>
                  Memorial Day weekend is one of the best-kept secrets for visiting{" "}
                  <a href="https://www.nps.gov/glac/index.htm" target="_blank" rel="noopener noreferrer">
                    Glacier National Park
                  </a>
                  . Although early in the season means some roads are still closed due to snow, that's actually part of the appeal — Going-to-the-Sun Road is car-free for bicyclists, the park is far less crowded than summer, and the wildflowers are just starting to emerge. It's raw, quiet, and breathtaking in a way that July and August simply aren't.
                </p>

                <h2>Why Memorial Day Weekend at Glacier?</h2>
                <p>
                  Summer crowds at Glacier can be overwhelming — timed entry reservations, packed trailheads, full campgrounds. Memorial Day sidesteps all of that. You'll share the park with a fraction of the summer visitors, mornings are crisp and clear, and the snow-covered peaks against blue skies make for some of the most dramatic photography of the year.
                </p>
                <p>
                  The biggest perk: Going-to-the-Sun Road is typically plowed to Avalanche Creek or beyond by Memorial Day, but still closed to vehicles. That means you can{" "}
                  <strong>bike or walk the most scenic road in America with no traffic</strong>. Cyclists ride past waterfalls, hanging valleys, and snowfields that most park visitors never see this way.
                </p>

                <h2>Day 1 — Arrive & West Glacier Base</h2>
                <p>
                  Fly into Kalispell (FCA) or Missoula and drive to West Glacier — about 35 minutes from Kalispell. Check in early and walk to the{" "}
                  <a href="https://www.nps.gov/glac/planyourvisit/apgar.htm" target="_blank" rel="noopener noreferrer">
                    Apgar Village
                  </a>{" "}
                  area along Lake McDonald. The lake is impossibly clear with multicolored pebbles on the bottom — a short evening stroll here sets the tone for the whole trip. Grab dinner at the Belton Chalet or pick up supplies in Whitefish (30 min north) for camp cooking.
                </p>

                <h2>Day 2 — Bike Going-to-the-Sun Road</h2>
                <p>
                  This is the highlight of a Memorial Day weekend trip. Rent bikes in Whitefish or bring your own, start at Apgar, and ride east on the closed section of Going-to-the-Sun Road. The road climbs steadily through old-growth cedar and hemlock forest, past{" "}
                  <strong>Bird Woman Falls</strong> and{" "}
                  <strong>Weeping Wall</strong>, offering unobstructed views since you're not dodging cars. Turn around when you hit the snow line or the road closure — typically somewhere between Avalanche Creek and Logan Pass depending on the season.
                </p>
                <ul>
                  <li><strong>Distance:</strong> 12–24 miles round trip depending on snow level</li>
                  <li><strong>Elevation gain:</strong> ~2,000 ft to the typical turnaround</li>
                  <li><strong>Bike rentals:</strong> Available in Whitefish and Columbia Falls</li>
                  <li><strong>Pro tip:</strong> Start by 8am — afternoons can bring clouds and wind</li>
                </ul>

                <h2>Day 3 — Trail of the Cedars & Avalanche Lake</h2>
                <p>
                  The{" "}
                  <a href="https://www.alltrails.com/trail/us/montana/avalanche-lake-trail" target="_blank" rel="noopener noreferrer">
                    Avalanche Lake Trail
                  </a>{" "}
                  is one of the most accessible hikes in Glacier and absolutely stunning in late May. The trail starts with the Trail of the Cedars loop — a flat, 0.9-mile boardwalk through ancient red cedar and western hemlock draped in moss. From there, continue 2 miles up to Avalanche Lake, a glacially-carved basin fed by multiple waterfalls cascading down the cliffs above.
                </p>
                <ul>
                  <li><strong>Distance:</strong> 5.9 miles round trip (Avalanche Lake + Cedars loop)</li>
                  <li><strong>Elevation gain:</strong> ~730 ft</li>
                  <li><strong>Difficulty:</strong> Moderate</li>
                  <li><strong>Trailhead:</strong> Avalanche Creek, accessible from Going-to-the-Sun Road</li>
                </ul>

                <h2>What to Pack for Memorial Day at Glacier</h2>
                <p>
                  Weather can swing dramatically — warm and sunny in the morning, snowing by afternoon. Layering is essential. Expect temperatures from 30°F to 60°F.
                </p>
                <ul>
                  <li>Waterproof jacket and pants</li>
                  <li>Base layer + mid-layer fleece</li>
                  <li>Microspikes if you plan to hike above the snow line</li>
                  <li>Sunscreen (UV intensity is high at elevation, even in snow)</li>
                  <li>Bear spray — bears are very active in early season</li>
                  <li>Trekking poles for any hike with snow</li>
                </ul>

                <h2>Where to Stay</h2>
                <p>
                  Most campgrounds inside Glacier don't open until mid-June, but{" "}
                  <strong>Apgar Campground</strong> typically opens around Memorial Day. Outside the park, Whitefish has excellent hotels, vacation rentals, and is a beautiful mountain town worth exploring on its own. Book 3–4 months in advance — even Memorial Day lodging fills up.
                </p>

                <h2>Getting There</h2>
                <p>
                  Fly into <strong>Glacier Park International Airport (FCA)</strong> in Kalispell — it's 35 minutes from the west entrance. Direct flights available from Seattle, Salt Lake City, Denver, Minneapolis, and Atlanta. Renting a car is essential — there is no public transit to the park in May.
                </p>
                <p>
                  To view the full Memorial Day weekend driving route, click <strong>"more options"</strong> on the Google Maps embed above.
                </p>

                <FAQAccordion
                  title="Glacier National Park Memorial Day FAQs"
                  items={[
                    { q: 'Is Glacier National Park open on Memorial Day weekend?', a: 'Yes — the park is open, but many roads and facilities are still closed due to snow. Going-to-the-Sun Road is typically only open up to Avalanche Creek or slightly beyond, making it ideal for car-free biking.' },
                    { q: "Do I need a timed entry permit for Memorial Day at Glacier?", a: "Timed entry permits are typically required May–September for vehicles entering the park between 6am–3pm. Check the park's recreation.gov page for the current year's requirements." },
                    { q: 'What trails are open at Glacier in late May?', a: 'Lower elevation trails like Trail of the Cedars, Avalanche Lake, and Johns Lake Loop are usually accessible. High-elevation trails near Logan Pass remain snow-covered.' },
                    { q: 'Can you bike Going-to-the-Sun Road on Memorial Day?', a: 'Yes — this is one of the best times to bike it. The road is plowed but closed to vehicles up to a certain point, giving cyclists exclusive access to a stunning mountain road.' },
                    { q: 'Where should I stay for Memorial Day weekend at Glacier?', a: 'West Glacier and Apgar Village are the most convenient base camps. Apgar Campground opens in late May. Many visitors stay in Whitefish or Kalispell if campgrounds are full.' },
                    { q: 'How cold is Glacier National Park in late May?', a: 'Daytime temperatures range from 40°F to 65°F. Mornings and evenings can be very cold, especially at elevation. Expect possible snow at higher altitudes and bring layers.' },
                    { q: 'How do I get to Glacier National Park?', a: 'Fly into Glacier Park International Airport (FCA) in Kalispell, or drive from Missoula (2 hours) or Great Falls (3 hours). The park entrance at West Glacier is the main gateway.' },
                  ]}
                />

              </div>
            </div>
          </div>
        </section>
      </React.Fragment>
    );
  }
}

export default GlacierInfo;
